import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, Lock } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuth';
import { apiError } from '../../lib/adminApi';
import { Input } from '../../components/ui/input';

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={location.state?.from || '/dashboard/leads'} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email, password);
      navigate(location.state?.from || '/dashboard/leads', { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0E1F3D]" data-testid="admin-login-page">
      <Helmet><title>Admin Sign In | Bruce Nelson Plumbing & Heating</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="hidden lg:flex flex-col justify-between p-14 text-white">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#F58220]">Bruce Nelson P&H</p>
          <p className="mt-2 font-oswald text-4xl uppercase">Admin Console</p>
        </div>
        <p className="max-w-sm text-sm text-slate-300 leading-relaxed">Every website inquiry and phone tap, in one place. Review new leads, assign owners and schedule follow-ups.</p>
        <p className="text-xs text-slate-500">Serving the Twin Cities since 1976</p>
      </div>
      <div className="flex items-center justify-center bg-[#F4F6F9] p-6 lg:rounded-l-3xl">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="admin-login-form">
          <div className="h-11 w-11 rounded-xl bg-[#14366C] grid place-items-center"><Lock className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="font-oswald text-3xl uppercase text-[#0E1F3D]">Sign in</h1>
            <p className="mt-1 text-sm text-slate-500">Authorized staff only.</p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-wider text-slate-600">Email</label>
            <Input id="admin-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" data-testid="admin-login-email" />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-wider text-slate-600">Password</label>
            <Input id="admin-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" data-testid="admin-login-password" />
          </div>
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#F58220] font-bold uppercase tracking-wider text-sm text-white transition-colors hover:bg-[#e07010] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="admin-login-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
